import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Film, UploadCloud, Download, Trash2, CheckCircle2, AlertTriangle, Loader2, Play, RefreshCw, Instagram, Clock, Copy } from "lucide-react";
import { api, mediaUrl, MODEL_LABELS, STATUS_LABELS } from "@/lib/api";

const TABS = [
  { key: "all", label: "Toutes" },
  { key: "rendering", label: "En cours" },
  { key: "ready", label: "Prêtes" },
  { key: "published", label: "Publiées" },
  { key: "failed", label: "Échouées" },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}j`;
}

function StatusBadge({ status }) {
  const map = {
    queued: "chip-warn",
    rendering: "chip-fb",
    ready: "chip-live",
    failed: "chip chip-warn",
    published: "chip-live",
  };
  return <span className={`chip ${map[status] || ""} !bg-white shadow-sm`}>{STATUS_LABELS[status] || status}</span>;
}

export default function VideoGallery({ videos, onRefresh }) {
  const [tab, setTab] = useState("all");
  const [preview, setPreview] = useState(null);
  const pollRef = useRef(null);

  const hasActive = videos.some(v => v.status === "queued" || v.status === "rendering");

  useEffect(() => {
    if (hasActive) {
      pollRef.current = setInterval(() => onRefresh?.(), 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [hasActive, onRefresh]);

  const filtered = tab === "all" ? videos : videos.filter(v => v.status === tab);

  const [fb, setFb] = useState({ configured: false });
  useEffect(() => { api.facebookStatus().then(setFb).catch(() => {}); }, []);
  const fbLive = fb.configured && !fb.error;
  const fbBusy = videos.some(v => ["uploading", "processing"].includes(v.fb_state) || ["uploading", "processing"].includes(v.ig_state));
  useEffect(() => {
    if (!fbBusy) return undefined;
    const id = setInterval(() => onRefresh?.(), 6000);
    return () => clearInterval(id);
  }, [fbBusy, onRefresh]);

  const prevFb = useRef({});
  useEffect(() => {
    videos.forEach(v => {
      const before = prevFb.current[v.id];
      if (["uploading", "processing"].includes(before) && before !== v.fb_state) {
        if (v.fb_state === "online") toast.success("Vidéo en ligne sur Facebook.");
        else if (v.fb_state === "queued") toast.message("Vidéo en attente : limite Facebook de 25/24 h atteinte. Elle partira automatiquement.");
        else if (v.fb_state === "error") toast.error(v.fb_error || "Échec de publication Facebook");
      }
      prevFb.current[v.id] = v.fb_state;
    });
  }, [videos]);

  const checkFb = async (video) => {
    try {
      const v = await api.facebookCheck(video.id);
      toast[v.fb_state === "online" ? "success" : "message"](v.fb_state === "online" ? "Vidéo en ligne sur Facebook." : `Statut Facebook : ${v.fb_state}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vérification impossible");
    }
  };

  const publish = async (video) => {
    const t = toast.loading("Publication en préparation…");
    try {
      await api.publishVideo(video.id);
      toast.success(fbLive ? `Envoi en cours sur ${fb.page_name || "votre page"}…` : "Vidéo marquée comme publiée. Connectez votre page Facebook dans Mon profil pour publier automatiquement.", { id: t });
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publication impossible", { id: t });
    }
  };

  const forceSend = async (video) => {
    try {
      await api.facebookRetry(video.id);
      toast.success("Envoi forcé en cours sur Facebook…");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    }
  };

  const copyCaption = async (video) => {
    try {
      const text = await api.videoCaption(video.id);
      await navigator.clipboard.writeText(text);
      toast.success("Légende et hashtags copiés");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Copie impossible");
    }
  };

  const remove = async (video) => {
    try {
      await api.deleteVideo(video.id);
      toast.success("Vidéo supprimée.");
      onRefresh?.();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <section className="card-surface p-6" data-testid="video-gallery">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold">Galerie des vidéos générées</h2>
          <p className="text-sm text-[color:var(--bcs-text-mut)] mt-1">
            Rendu, aperçu, publication Facebook — tout en un.
          </p>
        </div>
        <div className="flex gap-1 bg-[#f7f8fa] border border-[#dddfe2] rounded-lg p-1">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                tab === t.key
                  ? "bg-[#e7f3ff] text-[#1877f2]"
                  : "text-[color:var(--bcs-text-mut)] hover:text-[#1877f2]"
              }`}
              data-testid={`videos-tab-${t.key}`}
            >
              {t.label}
              {t.key !== "all" && (
                <span className="ml-1.5 text-[10px] font-mono-ui text-[color:var(--bcs-text-dim)]">
                  {videos.filter(v => v.status === t.key).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="py-14 text-center text-[color:var(--bcs-text-mut)]">
          <Film className="w-10 h-10 mx-auto opacity-40" />
          <div className="font-mono-ui text-xs tracking-widest uppercase mt-3">Aucune vidéo</div>
          <p className="text-sm mt-1">Génère ta première vidéo depuis le studio IA à droite.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((v) => (
          <article
            key={v.id}
            className="rounded-xl border border-[#dddfe2]/80 bg-[#ffffff] overflow-hidden flex flex-col fade-up"
            data-testid={`video-card-${v.id}`}
          >
            <div className="relative aspect-[9/16] bg-black overflow-hidden group">
              {v.video_url ? (
                <>
                  <video
                    src={mediaUrl(v.video_url)}
                    poster={mediaUrl(v.thumbnail_url) || undefined}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <button
                    type="button"
                    onClick={() => setPreview(v)}
                    className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors"
                    data-testid={`play-${v.id}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-5 h-5 text-black" fill="currentColor" />
                    </div>
                  </button>
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center">
                  {v.status === "failed" ? (
                    <>
                      <AlertTriangle className="w-8 h-8 text-amber-400 mb-2" />
                      <div className="text-xs text-[color:var(--bcs-text-mut)]">{v.error || "Échec"}</div>
                      {v.model === "spot" && v.spot_id && (
                        <button type="button" className="btn-ghost !py-1 mt-2 text-xs" data-testid={`spot-gallery-retry-${v.id}`}
                          onClick={() => api.spotRetry(v.spot_id).then(() => { toast.success("Spot relancé gratuitement"); onRefresh?.(); }).catch(e => toast.error(e?.response?.data?.detail || e.message))}>
                          Relancer gratuitement
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <Loader2 className="w-7 h-7 text-[color:var(--bcs-accent)] animate-spin mb-3" />
                      <div className="font-mono-ui text-[11px] tracking-widest uppercase text-[color:var(--bcs-text-dim)]">
                        {v.queued ? <span data-testid={`render-queued-${v.id}`}>En file d'attente</span> :
                          <>Rendu {v.model === "digest" ? "résumé" : v.model === "story" ? "histoire · ~5 min" : v.model === "spot" ? "spot pub · ~1 min" : v.model}</>}
                      </div>
                      <div className="w-32 h-1 rounded-full bg-[#e4e6eb] mt-3 overflow-hidden">
                        <div
                          className="h-full bg-[color:var(--bcs-accent)] transition-all"
                          style={{ width: `${Math.max(10, v.progress || 10)}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                <StatusBadge status={v.status} />
                <span className="chip font-mono-ui !bg-black/70 !text-white !border-transparent backdrop-blur">
                  {v.duration_sec}s · {v.aspect_ratio}
                </span>
              </div>
            </div>

            <div className="p-3.5 flex flex-col gap-2 flex-1">
              <div className="font-display font-semibold text-sm leading-snug line-clamp-2">
                {v.trend_title || v.prompt.slice(0, 60)}
              </div>
              <div className="text-[11px] text-[color:var(--bcs-text-dim)] font-mono-ui uppercase">
                {MODEL_LABELS[v.model]?.split(" · ")[0]} · il y a {timeAgo(v.created_at)}
              </div>
              <p className="text-xs text-[color:var(--bcs-text-mut)] line-clamp-2">{v.prompt}</p>

              {(["error", "queued"].includes(v.fb_state) && v.fb_error) && (
                <p className={`text-[11px] line-clamp-3 ${v.fb_state === "queued" ? "text-amber-600" : "text-red-600"}`} data-testid={`fb-error-text-${v.id}`}>{v.fb_error}</p>
              )}
              <div className="flex items-center gap-2 mt-auto pt-2">
                {v.fb_state === "uploading" || v.fb_state === "processing" ? (
                  <span className="btn-sharp flex-1 justify-center !text-[color:var(--bcs-accent)]" data-testid={`fb-processing-${v.id}`}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi Facebook…
                  </span>
                ) : v.fb_state === "queued" ? (
                  <button type="button" onClick={() => forceSend(v)} title="Envoyer maintenant"
                    className="btn-sharp flex-1 justify-center !border-amber-500 !text-amber-600" data-testid={`fb-queued-${v.id}`}>
                    <Clock className="w-3.5 h-3.5" /> En attente · Envoyer maintenant
                  </button>
                ) : v.fb_state === "error" ? (
                  <button type="button" onClick={() => publish(v)} title={v.fb_error || ""}
                    className="btn-sharp flex-1 justify-center !border-red-400 !text-red-600" data-testid={`fb-retry-${v.id}`}>
                    <UploadCloud className="w-3.5 h-3.5" /> Échec · Réessayer
                  </button>
                ) : v.status === "ready" && (
                  <button
                    type="button"
                    onClick={() => publish(v)}
                    className="btn-sharp flex-1 justify-center !border-[color:var(--bcs-accent)] !text-[color:var(--bcs-accent)] hover:!bg-[#1877f212]"
                    data-testid={`publish-${v.id}`}
                  >
                    <UploadCloud className="w-3.5 h-3.5" />
                    {fbLive ? (fb.via === "meta" ? `Publier · ${fb.networks.join(" + ")}` : "Publier sur Facebook") : "Marquer publiée"}
                  </button>
                )}
                {((v.status === "published" && v.fb_permalink) || v.fb_state === "online") && (
                  <a href={v.fb_permalink || `https://www.facebook.com/${v.fb_video_id}`} target="_blank" rel="noopener noreferrer"
                    className="btn-sharp flex-1 justify-center !border-green-600 !text-green-700" data-testid={`fb-online-${v.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Voir sur Facebook
                  </a>
                )}
                {v.fb_video_id && v.fb_state !== "uploading" && (
                  <button type="button" onClick={() => checkFb(v)} className="btn-sharp" title="Vérifier sur Facebook" data-testid={`fb-check-${v.id}`}>
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
                {v.status === "published" && !v.fb_permalink && !v.fb_state && (
                  <span className="btn-sharp flex-1 justify-center !border-green-700 !text-green-400 !bg-transparent">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Publiée
                  </span>
                )}
                {v.ig_state && (
                  v.ig_state === "online" && v.ig_permalink ? (
                    <a href={v.ig_permalink} target="_blank" rel="noopener noreferrer" title="Voir sur Instagram"
                      className="btn-sharp !border-pink-500 !text-pink-600" data-testid={`ig-online-${v.id}`}><Instagram className="w-3.5 h-3.5" /></a>
                  ) : v.ig_state === "error" ? (
                    <span title={v.ig_error || ""} className="btn-sharp !border-red-400 !text-red-600" data-testid={`ig-error-${v.id}`}><Instagram className="w-3.5 h-3.5" /> !</span>
                  ) : (
                    <span title="Envoi Instagram…" className="btn-sharp !text-pink-600" data-testid={`ig-processing-${v.id}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /></span>
                  )
                )}
                {v.video_url && (
                  <button type="button" onClick={() => copyCaption(v)} className="btn-sharp" title="Copier la légende" data-testid={`copy-caption-${v.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
                {v.video_url && (
                  <a
                    href={mediaUrl(v.video_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-sharp"
                    data-testid={`download-${v.id}`}
                    title="Télécharger"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => remove(v)}
                  className="btn-sharp !border-transparent hover:!border-red-900 hover:!text-red-400"
                  data-testid={`delete-${v.id}`}
                  title="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="video-preview-modal"
        >
          <div
            className="relative max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={mediaUrl(preview.video_url)}
              controls
              autoPlay
              className="w-full rounded-xl shadow-2xl"
            />
            <div className="mt-3 text-xs text-white/80">
              <div className="font-display font-semibold text-sm">{preview.trend_title || preview.prompt.slice(0, 80)}</div>
              <div className="mt-1 text-[color:var(--bcs-text-dim)] font-mono-ui">
                {MODEL_LABELS[preview.model]} · {preview.duration_sec}s
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute -top-10 right-0 text-white/70 hover:text-[#1877f2] text-sm"
              data-testid="close-preview"
            >
              Fermer ✕
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
